import { useMemo } from 'react';
import type { SourceTier, SubjectData, SubjectDataClaim } from '@ni/shared';
import { useSubjectData } from '../../api/hooks';
import { sourceTierLabel } from '../../lib/copy';

/**
 * **Where each fact came from, for the panel's margin** (blend spec, the
 * Research tab: "source and date in the margin, no click needed").
 *
 * The record read carries a fact's corroboration count and newest date but not
 * its sources; the Data tab's read (`GET /subjects/:id/data`) carries every
 * claim on every lineage. This indexes that read by lineage, so a row can name
 * its newest source and a panel can group its findings by the document they
 * were read from. Nothing here decides anything: a claim that was discarded or
 * arrived after its fact was removed is not a source of the fact as it stands.
 */

export interface FactSource {
  /** One document: the stored retrieval, the office's own note, or the address. */
  key: string;
  tier: SourceTier;
  url: string | null;
  date: string | null;
  /** Written by someone in the office rather than found by research. */
  office: boolean;
}

export type SourceIndex = Map<string, FactSource[]>;

const OFFICE_TIERS = new Set<SourceTier>([
  'office_correction',
  'office_observation',
  'club_supplied',
]);

function sourceOf(claim: SubjectDataClaim): FactSource {
  return {
    key: claim.evidence_id ?? claim.prose_input_id ?? claim.url ?? claim.claim_id,
    tier: claim.source_tier,
    url: claim.url,
    date: claim.content_date,
    office: OFFICE_TIERS.has(claim.source_tier) || claim.prose_input_id !== null,
  };
}

/** Each lineage's sources, newest first, one entry per document. */
export function sourceIndex(data: SubjectData): SourceIndex {
  const index: SourceIndex = new Map();
  for (const lineage of data.lineages) {
    const seen = new Set<string>();
    const sources = lineage.claims
      .filter((claim) => claim.resolution_status === 'resolved' && !claim.post_negation)
      .sort((a, b) => (b.content_date ?? '').localeCompare(a.content_date ?? ''))
      .map(sourceOf)
      .filter((source) => (seen.has(source.key) ? false : (seen.add(source.key), true)));
    index.set(lineage.lineage_id, sources);
  }
  return index;
}

/** How many distinct documents stand behind a set of facts. */
export function sourceCount(index: SourceIndex, lineages: string[]): number {
  const keys = new Set<string>();
  for (const lineage of lineages) {
    for (const source of index.get(lineage) ?? []) keys.add(source.key);
  }
  return keys.size;
}

/** The host of a stored address, as a quiet second line; never a link target. */
export function sourceHost(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** "Trade press": the tier in the org's words, set as a title. */
export function tierTitle(vertical: string, tier: SourceTier): string {
  const label = sourceTierLabel(vertical, tier);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function sourceTitle(vertical: string, source: FactSource): string {
  return tierTitle(vertical, source.tier);
}

/**
 * The index, built once for the whole tab. The panels work without it — the
 * margin then says what the record read knows — so a slow or refused read
 * costs the grouping, never the facts.
 */
export function useSourceIndex(subjectId: string, enabled: boolean): SourceIndex | null {
  const data = useSubjectData(subjectId, enabled);
  return useMemo(() => (data.data ? sourceIndex(data.data) : null), [data.data]);
}
